package er.prateek.crossly.ui.achievements

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.badges_count
import crossly.shared.generated.resources.badges_new
import crossly.shared.generated.resources.badges_subtitle
import crossly.shared.generated.resources.badges_title
import crossly.shared.generated.resources.badges_unlocked_on
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.progression.achievements.Achievement
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.GameTopBar
import er.prateek.crossly.ui.components.LabeledProgress
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

/**
 * The trophy cabinet. Unlocked badges show their medal and unlock date (tap one to replay its
 * celebration); locked ones say exactly how to earn them, which is what turns a badge list into
 * a set of goals.
 */
@Composable
fun AchievementsScreen(onBack: () -> Unit) {
    val colors = MaterialTheme.game
    val manager = AppContainer.achievementManager
    val unlockedCount = manager.unlockedCount()
    val total = Achievement.entries.size
    var replay by remember { mutableStateOf<Achievement?>(null) }

    LaunchedEffect(Unit) {
        AppContainer.analytics.logEvent(
            AnalyticsEvents.ACHIEVEMENTS_OPENED,
            mapOf("unlocked_count" to unlockedCount, "had_unseen" to manager.hasUnseen()),
        )
    }
    // Leaving clears the "new" dots; doing it on exit keeps them visible while the player looks.
    DisposableEffect(Unit) { onDispose { manager.markAllSeen() } }

    GameScreen(
        overlay = {
            replay?.let { AchievementUnlockedOverlay(achievement = it, onDismiss = { replay = null }) }
        },
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            GameTopBar(
                title = stringResource(Res.string.badges_title),
                subtitle = stringResource(Res.string.badges_subtitle),
                onBack = onBack,
            )
            LazyVerticalGrid(
                columns = GridCells.Adaptive(minSize = 150.dp),
                modifier = Modifier.fillMaxSize(),
                horizontalArrangement = Arrangement.spacedBy(Spacing.md),
                verticalArrangement = Arrangement.spacedBy(Spacing.md),
            ) {
                item(span = { GridItemSpan(maxLineSpan) }) {
                    GameCard(modifier = Modifier.fillMaxWidth().padding(top = Spacing.md)) {
                        LabeledProgress(
                            label = stringResource(Res.string.badges_title),
                            value = stringResource(Res.string.badges_count, unlockedCount, total),
                            progress = if (total > 0) unlockedCount.toFloat() / total else 0f,
                            tint = colors.gold,
                        )
                    }
                }
                items(manager.shelf(), key = { it.id }) { achievement ->
                    BadgeCell(
                        achievement = achievement,
                        unlocked = manager.isUnlocked(achievement),
                        isNew = manager.isUnseen(achievement),
                        unlockedAtMs = manager.unlockedAtMs(achievement),
                        onClick = { replay = achievement },
                    )
                }
            }
        }
    }
}

@Composable
private fun BadgeCell(
    achievement: Achievement,
    unlocked: Boolean,
    isNew: Boolean,
    unlockedAtMs: Long?,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game
    GameCard(
        modifier = Modifier.fillMaxWidth(),
        onClick = if (unlocked) onClick else null,
        accentGlow = if (unlocked) achievement.rarity.color().copy(alpha = 0.55f) else null,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(Spacing.xs),
        ) {
            Box {
                AchievementMedal(achievement = achievement, unlocked = unlocked, size = 72.dp)
                if (isNew) NewTag(Modifier.align(Alignment.TopEnd))
            }
            Text(
                text = stringResource(achievement.titleRes),
                style = MaterialTheme.typography.titleMedium,
                color = if (unlocked) colors.ink else colors.inkMuted,
                textAlign = TextAlign.Center,
                maxLines = 1,
                modifier = Modifier.padding(top = Spacing.xs),
            )
            Text(
                text = stringResource(achievement.descriptionRes),
                style = MaterialTheme.typography.bodySmall,
                color = colors.inkMuted,
                textAlign = TextAlign.Center,
                minLines = 2,
                maxLines = 2,
            )
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = Spacing.xs),
                horizontalArrangement = Arrangement.Center,
            ) {
                if (unlocked && unlockedAtMs != null) {
                    Text(
                        text = stringResource(Res.string.badges_unlocked_on, formatShortDate(unlockedAtMs)),
                        style = MaterialTheme.typography.labelSmall,
                        color = achievement.rarity.color(),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )
                } else {
                    RarityPill(achievement.rarity)
                }
            }
        }
    }
}

@Composable
fun NewTag(modifier: Modifier = Modifier) {
    val colors = MaterialTheme.game
    Text(
        text = stringResource(Res.string.badges_new),
        style = MaterialTheme.typography.labelSmall,
        color = colors.onAccent,
        modifier = modifier
            .background(colors.flame, RoundedCornerShape(Radius.chip))
            .padding(horizontal = 6.dp, vertical = 1.dp),
    )
}

private val MONTHS = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

private fun formatShortDate(epochMs: Long): String {
    val date = Instant.fromEpochMilliseconds(epochMs).toLocalDateTime(TimeZone.currentSystemDefault()).date
    return "${MONTHS[date.month.ordinal]} ${date.day}, ${date.year}"
}
